import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  alpha,
  Typography,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { StandardizedOnboardingData } from "./utils/onboardingDataTransformer";
import {
  ValidationErrors,
  validateCompleteData,
} from "./utils/onboardingStep3Utils";
import {
  Header,
  OperatingHoursOnBoarding3,
  StoreOverview,
  SubscriptionPlan,
} from "./components";
import { SubscriptionPlanType } from "./components/SubscriptionPlan";

interface OnBoardingStep3Props {
  onContinue: (standardizedData: StandardizedOnboardingData) => void;
  onBack: () => void;
  onEdit: () => void;
  standardizedData: StandardizedOnboardingData | null;
}

const ValidationStatus: React.FC<{
  validation: ValidationErrors;
  onEdit: () => void;
}> = ({ validation, onEdit }) => {
  const { t } = useTranslation();

  return (
    <>
      {validation.errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>{t(messages.Required_Information_Missing())}</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validation.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={onEdit}
            sx={{ mt: 1 }}
          >
            {t(messages.Edit_Store_Details())}
          </Button>
        </Alert>
      )}

      {validation.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>{t(messages.Recommended_Information())}</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validation.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={onEdit}
            sx={{ mt: 1 }}
          >
            {t(messages.Add_Optional_Details())}
          </Button>
        </Alert>
      )}

      {validation.isValid && validation.warnings.length === 0 && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <AlertTitle>{t(messages.Ready_to_Complete_Setup())}</AlertTitle>
          {t(
            messages.All_required_information_has_been_provided_Your_store_is_ready_to_go_live()
          )}
        </Alert>
      )}
    </>
  );
};

const ActionButtons: React.FC<{
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  isValid: boolean;
}> = ({ onBack, onSubmit, isSubmitting, isValid }) => {
  const { t } = useTranslation();

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      mt={4}
      sx={{
        position: "sticky",
        bottom: 24,
        zIndex: 10,
      }}
    >
      <Button
        variant="outlined"
        size="large"
        onClick={onBack}
        disabled={isSubmitting}
        sx={{
          minWidth: { xs: 120, sm: 160 },
          height: 56,
          fontSize: "1rem",
          fontWeight: 600,
          borderRadius: 28,
          borderColor: "#4caf50",
          color: "#4caf50",
          "&:hover": {
            borderColor: "#43a047",
            backgroundColor: alpha("#4caf50", 0.05),
          },
        }}
      >
        {t(messages.Back())}
      </Button>

      <Button
        variant="contained"
        size="large"
        onClick={onSubmit}
        disabled={!isValid || isSubmitting}
        sx={{
          minWidth: { xs: 200, sm: 240 },
          height: 56,
          fontSize: "1.125rem",
          fontWeight: 600,
          borderRadius: 28,
          backgroundColor: "#4caf50",
          boxShadow: "0 8px 24px rgba(76, 175, 80, 0.4)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: "#43a047",
            transform: "translateY(-2px)",
            boxShadow: "0 12px 32px rgba(76, 175, 80, 0.5)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
          "&:disabled": {
            backgroundColor: alpha("#4caf50", 0.3),
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            transform: "none",
          },
        }}
      >
        {isSubmitting ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
            {t(messages.Setting_up_store())}
          </>
        ) : (
          t(messages.Complete_Setup())
        )}
      </Button>
    </Box>
  );
};

export const OnBoardingStep3: React.FC<OnBoardingStep3Props> = ({
  onContinue,
  onBack,
  onEdit,
  standardizedData,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validation, setValidation] = useState<ValidationErrors>({
    isValid: false,
    errors: [],
    warnings: [],
  });
  const [selectedPlan, setSelectedPlan] =
    useState<SubscriptionPlanType["id"]>("free");
  const [updatedData, setUpdatedData] =
    useState<StandardizedOnboardingData | null>(standardizedData);

  useEffect(() => {
    const validationResult = validateCompleteData(updatedData);
    setValidation(validationResult);
  }, [updatedData]);

  useEffect(() => {
    if (standardizedData) {
      setUpdatedData(standardizedData);
      setSelectedPlan(standardizedData.subscription?.planId || "free");
    }
  }, [standardizedData]);

  const handlePlanSelect = (planId: SubscriptionPlanType["id"]) => {
    setSelectedPlan(planId);
    if (updatedData) {
      const planName =
        planId === "free"
          ? t(messages.Free_Plan_Text())
          : t(messages.Pro_Plan_Text());
      setUpdatedData({
        ...updatedData,
        subscription: {
          planId,
          planName,
        },
      });
    }
  };

  const handleSubmit = async () => {
    if (!updatedData || !validation.isValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onContinue(updatedData);
    } catch (error) {
      console.error("Error submitting onboarding:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!updatedData) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          {t(
            messages.No_store_data_available_Please_go_back_and_complete_the_previous_steps()
          )}
        </Typography>
        <Button onClick={onBack} sx={{ mt: 2 }}>
          {t(messages.Go_Back())}
        </Button>
      </Box>
    );
  }

  const { store } = updatedData;

  return (
    <Box sx={{ width: "100%" }}>
      <Header />

      <ValidationStatus validation={validation} onEdit={onEdit} />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <StoreOverview standardizedData={updatedData} onEdit={onEdit} />
        </Grid>

        <Grid item xs={12}>
          <SubscriptionPlan
            selectedPlan={selectedPlan}
            onPlanSelect={handlePlanSelect}
          />
        </Grid>

        {store?.openingHours && store.openingHours.length > 0 && (
          <Grid item xs={12}>
            <OperatingHoursOnBoarding3 openingHours={store.openingHours} />
          </Grid>
        )}
      </Grid>

      <ActionButtons
        onBack={onBack}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isValid={validation.isValid}
      />
    </Box>
  );
};
